import { LitElement, html, css, nothing } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { map } from 'lit/directives/map.js';
import { when } from 'lit/directives/when.js';
import { styleMap } from 'lit/directives/style-map.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import '@shoelace-style/shoelace/dist/components/menu-label/menu-label.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/progress-ring/progress-ring.js';
import '../katapult-elements/katapult-button.js';
import '../katapult-elements/katapult-icon.js';
import { getPercentElapsedForTimer } from '../../modules/katapult-workflows/WorkflowTimers.js';
import { GetTextColor, GetHoverColor } from '../../modules/ColorTools.js';

/** @import { Ref } from 'lit/directives/ref.js' */

export class OverviewActionsButton extends LitElement {
  static properties = {
    jobId: { type: String },
    jobName: { type: String },
    jobModel: { type: String },
    jobMetadata: { type: Object },
    userGroup: { type: String },
    enabledFeatures: { type: Object },
    attachers: { type: Object },
    dropdownOpen: { type: Boolean, reflect: true, attribute: 'dropdown-open' },
    _actionButtons: { type: Array, state: true },
    _timers: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _loadError: { type: Boolean, state: true }
  };

  static styles = css`
    :host {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    sl-progress-ring {
      --size: 16px;
      --track-width: 2px;
    }
    sl-menu-item {
      --sl-color-neutral-700: var(--action-item-text-color, var(--sl-color-neutral-700));
      --sl-color-primary-600: var(--action-item-hover-color, var(--sl-color-neutral-200));
      --sl-color-neutral-0: var(--action-item-hover-text-color, var(--sl-color-neutral-1000));
    }
  `;

  constructor() {
    super();
    this.jobId = null;
    this.jobName = null;
    this.jobModel = null;
    this.jobMetadata = null;
    this.userGroup = null;
    this.enabledFeatures = null;
    this.attachers = null;
    // Used by pole-application-overview to set the z-index of the row when the dropdown is open
    this.dropdownOpen = false;
    this._actionButtons = [];
    this._timers = [];
    this._loading = false;
    this._loadError = false;

    /** @type {Ref<import('@shoelace-style/shoelace').SlDropdown>} */
    this._dropdownRef = createRef();
  }

  render() {
    if (!this.jobMetadata?.app_type) return nothing;

    const { _actionButtons: actionButtons, _timers: timers } = this;
    const hasContent = actionButtons.length > 0 || timers.length > 0;

    return html`
      <sl-dropdown
        ${ref(this._dropdownRef)}
        hoist
        @sl-show=${this.#handleDropdownShow.bind(this)}
        @sl-after-show=${this.#handleDropdownAfterShow.bind(this)}
        @sl-after-hide=${this.#handleDropdownAfterHide.bind(this)}
      >
        <katapult-button
          slot="trigger"
          iconOnly
          noBorder
          icon="bolt"
          loader-in-button
          .loading=${this._loading}
          title="Workflow actions"
        ></katapult-button>
        <sl-menu @sl-select=${this.#handleMenuSelect.bind(this)}>
          ${when(
            this._loadError,
            () => html`<sl-menu-item disabled>Failed to load actions</sl-menu-item>`,
            () =>
              when(
                hasContent,
                () => html`
                  ${when(
                    actionButtons.length > 0,
                    () => html`
                      <sl-menu-label>Actions</sl-menu-label>
                      ${map(actionButtons, (button, index) => this.#displayActionMenuItem(button, index))}
                    `
                  )}
                  ${when(actionButtons.length > 0 && timers.length > 0, () => html`<sl-divider></sl-divider>`)}
                  ${when(
                    timers.length > 0,
                    () => html`
                      <sl-menu-label>Timers</sl-menu-label>
                      ${map(
                        timers,
                        (timer, index) => html`
                          <sl-menu-item value=${`timer:${index}`}>
                            <sl-progress-ring slot="prefix" .value=${getPercentElapsedForTimer(timer)}></sl-progress-ring>
                            ${timer.label}
                          </sl-menu-item>
                        `
                      )}
                    `
                  )}
                `,
                () => html`<sl-menu-item disabled>No actions available</sl-menu-item>`
              )
          )}
        </sl-menu>
      </sl-dropdown>
    `;
  }

  /**
   * @param {Object} button
   * @param {number} index
   */
  #displayActionMenuItem(button, index) {
    const textColor = button.color ? GetTextColor(button.color, this) : null;
    const hoverColor = button.color ? GetHoverColor(button.color, this) : null;
    const hoverTextColor = hoverColor ? GetTextColor(hoverColor, this) : null;
    const styles = {
      backgroundColor: button.color || '',
      '--action-item-text-color': textColor || '',
      '--action-item-hover-color': hoverColor || '',
      '--action-item-hover-text-color': hoverTextColor || ''
    };
    return html`
      <sl-menu-item value=${`action:${index}`} style=${styleMap(styles)}>
        ${when(button.icon, () => html`<katapult-icon slot="prefix" icon=${button.icon} color=${textColor ?? nothing}></katapult-icon>`)}
        ${button.label ?? button.$key}
      </sl-menu-item>
    `;
  }

  /**
   * Get cache from overview and show dropdown
   * @param {CustomEvent} event
   */
  async #handleDropdownShow(event) {
    event.preventDefault();
    if (this._loading) return;

    const { jobId, jobModel, jobMetadata, userGroup } = this;
    if (!jobId || !jobModel || !jobMetadata?.app_type) return;

    this._loading = true;
    this._loadError = false;

    // The promise will be set and resolved by the overview with the custom event
    const detail = { source: this, jobId, jobModel, jobMetadata, userGroup, attachers: this.attachers, promise: null };
    this.dispatchEvent(new CustomEvent('request-actions-context', { bubbles: true, composed: true, detail }));

    try {
      const context = await detail.promise;
      // Make sure the jobId hasn't changed while we were waiting for the promise to resolve
      if (this.jobId !== jobId) return;
      this._actionButtons = context?.actionButtons ?? [];
      this._timers = context?.timers ?? [];
    } catch (error) {
      this._loadError = true;
      console.error('Failed to load workflow actions for job', jobId, error);
    } finally {
      this._loading = false;
    }

    this._dropdownRef.value?.show();
  }

  #handleDropdownAfterShow() {
    this.dropdownOpen = true;
  }

  #handleDropdownAfterHide() {
    this.dropdownOpen = false;
  }

  /** @param {CustomEvent} event */
  #handleMenuSelect(event) {
    const value = event.detail?.item?.value;
    if (typeof value !== 'string') return;

    const [type, indexString] = value.split(':');
    const index = Number(indexString);

    if (type === 'action') {
      const button = this._actionButtons?.[index];
      if (button) this.#requestOpenActionDialog(button);
    } else if (type === 'timer') {
      const timer = this._timers?.[index];
      if (timer) this.#requestOpenTimerDialog(timer);
    }
  }

  /** @param {Object} button */
  #requestOpenActionDialog(button) {
    this.dispatchEvent(
      new CustomEvent('open-workflow-action-dialog', {
        bubbles: true,
        composed: true,
        detail: {
          source: this,
          button,
          jobId: this.jobId,
          jobName: this.jobName,
          jobModel: this.jobModel,
          jobMetadata: this.jobMetadata,
          userGroup: this.userGroup,
          enabledFeatures: this.enabledFeatures ?? {}
        }
      })
    );
  }

  /** @param {import('../../modules/katapult-workflows/WorkflowTimers.js').Timer} timer */
  #requestOpenTimerDialog(timer) {
    this.dispatchEvent(
      new CustomEvent('open-workflow-timer-dialog', {
        bubbles: true,
        composed: true,
        detail: { source: this, timer }
      })
    );
  }
}

customElements.define('overview-actions-button', OverviewActionsButton);
